import React, { useEffect, useState } from "react";
import {
  Modal,
  Radio,
  Space,
  Typography,
  Alert,
  Input,
  Button,
  message,
} from "antd";

const { Text, Paragraph } = Typography;

/**
 * Dialogo de confirmacion para eliminar un LiveStreamGame.
 *
 * Logica:
 *   1. Al abrir, consulta sessionCount.
 *   2. Si count === 0: opciones radio "Solo desactivar" / "Eliminar definitivamente".
 *   3. Si count > 0: solo "Solo desactivar" (hard-delete disabled).
 *   4. Hard-delete requiere typing del gameKey como confirmacion doble.
 *
 * Props:
 *   - open: bool
 *   - game: object | null
 *   - onClose()
 *   - onConfirm({ mode: "soft"|"hard" })
 *   - sessionCountFetcher(gameId) -> Promise<number>
 */
export function DeleteConfirmDialog({
  open,
  game,
  onClose,
  onConfirm,
  sessionCountFetcher,
}) {
  const [count, setCount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("soft");
  const [typedKey, setTypedKey] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !game) return;
    setMode("soft");
    setTypedKey("");
    setCount(null);
    setLoading(true);
    sessionCountFetcher(game.id)
      .then((c) => setCount(c))
      .catch(() => setCount(0))
      .finally(() => setLoading(false));
  }, [open, game, sessionCountFetcher]);

  if (!game) return null;

  const canHardDelete = count === 0;
  const isHard = mode === "hard";
  const hardConfirmed = isHard && typedKey.trim() === game.gameKey;

  const handleConfirm = async () => {
    if (isHard && !hardConfirmed) {
      message.warning("Escribe el gameKey exacto para confirmar el borrado");
      return;
    }
    setSubmitting(true);
    try {
      await onConfirm({ mode });
      onClose();
    } catch (e) {
      message.error(e?.message || "Error al eliminar");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={`Eliminar: ${game.label}`}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancelar
        </Button>,
        <Button
          key="ok"
          type="primary"
          danger
          loading={submitting}
          disabled={isHard && !hardConfirmed}
          onClick={handleConfirm}
        >
          {isHard ? "Eliminar definitivamente" : "Desactivar"}
        </Button>,
      ]}
      destroyOnClose
    >
      {loading ? (
        <Text>Comprobando sesiones historicas...</Text>
      ) : (
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <Text>
            Este juego tiene <Text strong>{count}</Text> sesion(es) historica(s)
            asociadas.
          </Text>

          <Radio.Group value={mode} onChange={(e) => setMode(e.target.value)}>
            <Space direction="vertical">
              <Radio value="soft">
                <Text strong>Solo desactivar</Text> (recomendado)
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  El juego deja de aceptar nuevos streams pero el historico de
                  sesiones se preserva. Puedes reactivar en cualquier momento.
                </Text>
              </Radio>
              <Radio value="hard" disabled={!canHardDelete}>
                <Text strong>Eliminar definitivamente</Text>{" "}
                {!canHardDelete && (
                  <Text type="secondary">(no disponible, hay sesiones)</Text>
                )}
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Borra el juego y su icono del bucket. Solo permitido si no hay
                  sesiones historicas asociadas. Irreversible.
                </Text>
              </Radio>
            </Space>
          </Radio.Group>

          {isHard && (
            <Alert
              type="warning"
              showIcon
              message="Confirmacion doble"
              description={
                <Paragraph style={{ marginBottom: 8 }}>
                  Para confirmar borrado definitivo, escribe el gameKey:{" "}
                  <Text code>{game.gameKey}</Text>
                </Paragraph>
              }
            />
          )}

          {isHard && (
            <Input
              placeholder={game.gameKey}
              value={typedKey}
              onChange={(e) => setTypedKey(e.target.value)}
              autoFocus
            />
          )}
        </Space>
      )}
    </Modal>
  );
}
